import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowUpRight, Cpu } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/lib/motion";
import { PRODUCTS } from "@/data/content";
import { useEnquiry } from "@/context/EnquiryContext";

export default function Products() {
  const [q, setQ] = useState("");
  const { openEnquiry } = useEnquiry();
  const filtered = PRODUCTS.filter((p) =>
    (p.name + p.short + p.applications.join(" ")).toLowerCase().includes(q.toLowerCase())
  );

  return (
    <section id="products" data-testid="products" className="bg-brand-cream py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand-brown mb-4">Our Products</p>
            <h2 className="font-heading font-bold text-brand-ink tracking-tight text-3xl sm:text-5xl max-w-2xl leading-[1.05]">
              Premium farm implements built for modern farming.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative w-full lg:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-brand-ink/40" />
              <Input
                data-testid="product-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search machinery…"
                className="pl-11 h-12 rounded-full bg-white border-brand-ink/15"
              />
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.08}>
              <article data-testid={`product-card-${p.slug}`} className="group h-full flex flex-col rounded-2xl border border-brand-ink/10 bg-white overflow-hidden hover:shadow-2xl hover:-translate-y-1.5 transition-[transform,box-shadow] duration-300">
                <Link to={`/products/${p.slug}`} className="block relative spotlight overflow-hidden">
                  <span className="absolute top-4 left-4 z-10 font-mono text-xs font-bold text-brand-green bg-brand-cream/90 backdrop-blur px-2.5 py-1 rounded-full">{p.index}</span>
                  <motion.img
                    src={p.image}
                    alt={p.name}
                    className="h-56 w-full object-cover clip-frame group-hover:scale-105 transition-transform duration-500"
                  />
                </Link>
                <div className="flex flex-col flex-1 p-6">
                  <h3 className="font-heading font-bold text-xl text-brand-ink tracking-tight leading-tight">{p.name}</h3>
                  <p className="mt-2 text-sm text-brand-ink/65 leading-relaxed">{p.short}</p>
                  <div className="mt-4 flex items-center gap-2 text-xs font-mono text-brand-blue">
                    <Cpu className="size-3.5" /> Requires {p.hp}
                  </div>
                  <div className="mt-6 pt-5 border-t border-brand-ink/10 flex items-center gap-3">
                    <Button data-testid={`enquire-${p.slug}`} onClick={() => openEnquiry(p.name, "enquiry")} className="rounded-full bg-brand-green text-white hover:bg-brand-greenDark flex-1">
                      Enquire Now
                    </Button>
                    <Link to={`/products/${p.slug}`} data-testid={`details-${p.slug}`} aria-label={`View ${p.name}`} className="grid place-items-center size-10 rounded-full border border-brand-ink/15 text-brand-ink hover:bg-brand-ink hover:text-white transition-colors">
                      <ArrowUpRight className="size-4" />
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {filtered.length === 0 && (
          <p data-testid="no-products" className="mt-12 text-center text-brand-ink/50">No machinery matches “{q}”. Try another search.</p>
        )}
      </div>
    </section>
  );
}
